import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import React from 'react';

import { DataAPIContext, GET_ONE } from '../api';

/**
 * CP-8335: an operator can run several branded web widgets (one per website). TourOptima
 * staff add them (backend `manage.py web_widget_profiles`); the dashboard edits each one.
 * An operator with a single widget gets an empty list and sees no switcher at all.
 *
 * Widget shape (GET /v2/edit/web-chat/widgets/): {id, key, name, display_name, is_default,
 * knowledge_mode, restricted, access_groups: [{id, name}], live, config_key}.
 */
export const useWebWidgets = (operatorCode) => {
  const dataAPI = React.useContext(DataAPIContext);
  const [widgets, setWidgets] = React.useState([]);
  React.useEffect(() => {
    if (!dataAPI || !operatorCode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await dataAPI(GET_ONE, 'web-chat', { urlSuffix: 'widgets/', operator_id: operatorCode });
        if (!cancelled && res && res.statusCode === 200 && Array.isArray(res.data?.widgets)) {
          setWidgets(res.data.widgets);
        }
      } catch (e) {
        // non-fatal: an older backend has no endpoint -> one widget, no switcher
      }
    })();
    return () => { cancelled = true; };
  }, [dataAPI, operatorCode]);
  return widgets;
};

/** The widget named by ?widget=<key>, else the default one (null when there are none). */
export const selectedWidget = (widgets, key) => {
  if (!widgets || widgets.length === 0) return null;
  return widgets.find((w) => w.key === key) || widgets.find((w) => w.is_default) || widgets[0];
};

const knowledgeLabel = (w) => (w.knowledge_mode === 'isolated'
  ? 'Uses only its own documents and replies'
  : 'Uses shared documents and replies plus its own');

const WebWidgetSwitcher = ({ widgets, value, onChange }) => {
  if (!widgets || widgets.length < 2 || !value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, py: 1.5 }}>
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel id="web-widget-switcher-label">Widget</InputLabel>
        <Select
          labelId="web-widget-switcher-label"
          label="Widget"
          value={value.key}
          onChange={(e) => onChange(widgets.find((w) => w.key === e.target.value))}
        >
          {widgets.map((w) => (
            <MenuItem key={w.id} value={w.key}>
              {w.display_name || w.name}{w.is_default ? ' (main)' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Chip size="small" label={value.live ? 'Live' : 'Not live'} color={value.live ? 'success' : 'default'} />
        <Chip size="small" variant="outlined" label={knowledgeLabel(value)} />
        {value.restricted && (
          <Chip
            size="small"
            variant="outlined"
            label={`Chats visible to: ${value.access_groups.map((g) => g.name).join(', ')}`}
          />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
        Each widget has its own look, embed snippet, conversations and metrics. Widgets are added by TourOptima.
      </Typography>
    </Box>
  );
};

export default WebWidgetSwitcher;
